import { API_CONFIG } from '@msrllabs/pt-api';
import {
  AdjustmentType,
  BankAccount,
  CapitalPayOrderRequest,
  CapitalPayOrderResponse,
  CheckoutRequest,
  CheckoutType,
  ConversionQuote,
  ConversionQuoteRequest,
  CreditCard,
  DepositInfo,
  DepositMethodLimit,
  DepositPaymentMethod,
  DepositRequest,
  DepositWithdraw,
  EpicPayBankOption,
  EpicPayOrderRequest,
  EpicPayOrderResponse,
  LiveAccountRegistration,
  Page,
  SignedCurrencyConversionRate,
  TxDetail,
  TxHistory,
  WithdrawalDetails,
  WithdrawalInfo,
  WithdrawalRequest,
} from '@msrllabs/pt-api';

import { fetchJson } from './apiUtils';

export const createBankAccountWithStatement = (
  cognitoId: string,
  {
    bankAccountNumber,
    bankName,
    accountHolderName,
    file,
  }: {
    bankAccountNumber: string;
    bankName: string;
    accountHolderName?: string;
    file: File;
  }
) => {
  const formData = new FormData();
  formData.append('bankAccountNumber', bankAccountNumber);
  formData.append('bankName', bankName);
  if (accountHolderName) formData.append('accountHolderName', accountHolderName);
  formData.append('files', file);

  return fetchJson<BankAccount>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${encodeURIComponent(cognitoId)}/bankAccounts/withStatement`,
    { body: formData, method: 'POST' }
  );
};

// order_id is assigned by the backend per attempt, from the signed quote sent with it.
export type BridgerPayCheckoutRequest = Omit<CheckoutRequest, 'order_id'> & {
  accountId: string;
  currencyConversionRate: SignedCurrencyConversionRate;
};

export type BridgerPayCheckout = {
  cashierKey: string;
  embedUrl: string;
  checkoutToken: string;
  checkoutUrl: string;
};

export const createCheckout = (accountType: string, checkoutRequest: BridgerPayCheckoutRequest, checkoutType: CheckoutType) => {
  return fetchJson<BridgerPayCheckout>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v2/${accountType}/payins/bridgerPay/checkouts/${checkoutType}`,
    {
      body: JSON.stringify(checkoutRequest),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    }
  );
};

export const createCapitalPayOrder = (accountType: string, request: CapitalPayOrderRequest) =>
  fetchJson<CapitalPayOrderResponse>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v2/${accountType}/payins/capitalPay/orders`, {
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

export const createEpicPayOrder = (depositType: string, request: EpicPayOrderRequest) =>
  fetchJson<EpicPayOrderResponse>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v2/payins/epicPay/checkouts/${depositType}`, {
    body: JSON.stringify(request),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

export const getEpicPayBankOptions = (depositType: string) =>
  fetchJson<EpicPayBankOption[]>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v2/payins/epicPay/bankOptions/${depositType}`);

export const createConversionQuote = (req: ConversionQuoteRequest) =>
  fetchJson<ConversionQuote>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/conversionQuotes`, {
    body: JSON.stringify(req),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

export const createDepositRequest = (depositRequest: DepositRequest) =>
  fetchJson<DepositRequest>(`/aqt/cash/v1/depositRequests`, {
    body: JSON.stringify(depositRequest),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

export const createWithdrawalRequest = (withdrawalRequest: WithdrawalRequest) => {
  return fetchJson<WithdrawalRequest>(
    `/aqt/trading_${encodeURIComponent(withdrawalRequest.accountType.toLowerCase())}/v2/withdrawalRequests`,
    {
      body: JSON.stringify(withdrawalRequest),
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
    }
  );
};

export const getAdjustments = async (
  cognitoId: string,
  accountType: string,
  accountId: string,
  fr: Date,
  to: Date,
  page: number,
  size: number,
  sortField: string,
  isDesc: boolean,
  adjustmentTypes?: string,
  notInAdjustmentTypes?: string
) => {
  const endpointUrl = `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/users/${encodeURIComponent(cognitoId)}/accounts/${encodeURIComponent(
    accountType
  )}/${accountId}/transactions/adjustments`;
  const queryParams = new URLSearchParams();
  queryParams.set('fr', fr.toISOString());
  queryParams.set('to', to.toISOString());
  queryParams.set('page', page.toString());
  queryParams.set('size', size.toString());
  queryParams.set('sortField', sortField);
  queryParams.set('isDesc', isDesc.toString());
  if (adjustmentTypes) queryParams.set('adjustmentTypes', adjustmentTypes);
  if (notInAdjustmentTypes) queryParams.set('notInAdjustmentTypes', notInAdjustmentTypes);
  return fetchJson<Page<TxHistory<AdjustmentType>>>(`${endpointUrl}?${queryParams.toString()}`);
};

export const getBankAccounts = (cognitoId: string) => {
  return fetchJson<(BankAccount & { imageId?: string | null })[]>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${encodeURIComponent(cognitoId)}/bankAccounts`
  );
};

export const getConversionQuote = (id: string) =>
  fetchJson<ConversionQuote>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/conversionQuotes/${encodeURIComponent(id)}`);

export const getCreditCards = async (cognitoId: string, accountType: string, accountId: string) => {
  const result = await fetchJson<{ data: CreditCard[] }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/users/${cognitoId}/accounts/${accountType}/${accountId}/withdrawals/creditCards`
  );
  return result.data;
};

export const getDepositAndWithdrawal = (cognitoId: string, accountType: string, accountId: string, fr: Date, to: Date) => {
  return fetchJson<Page<TxHistory<DepositWithdraw>>>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/users/${encodeURIComponent(cognitoId)}/accounts/${encodeURIComponent(
      accountType
    )}/${accountId}/transactions/deposit-withdrawal?fr=${fr.toISOString()}&to=${to.toISOString()}`
  );
};

export const getDepositInfo = async () => {
  return await fetchJson<DepositInfo>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/depositRequests/depositInfo`);
};

export const getDepositDetails = async () => {
  const response = await fetch(`${API_CONFIG.PT_WEB_ENDPOINT}/labels/v4/deposit.json`);
  return response.json();
};

export const getDepositLimit = (accountType: string, accountId: string) => {
  return fetchJson<{ depositMethodLimits: Array<DepositMethodLimit> }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v2/depositLimits/${accountType}/${accountId}`
  );
};

export const getPaymentDetails = async () => {
  const response = await fetch(`${API_CONFIG.PT_WEB_ENDPOINT}/labels/v4/payment.json`);
  return response.json();
};

export const getLiveAccountRegistrations = (cognitoId: string) => {
  return fetchJson<Page<LiveAccountRegistration>>(`/aqt/cash/v1/liveAccountRegistrations?cognitoId=${cognitoId}`);
};

export const getMerchantReference = (
  accountType: string,
  accountId: string,
  paymentMethod: DepositPaymentMethod,
  signedCurrencyConversionRate: Record<string, string>
) => {
  const accountCode = `${accountType}:${accountId}`;
  return fetchJson<{ paymentCode: string }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/accountCodes/${accountCode}/paymentCodeWithConversionRates/${paymentMethod}`,
    {
      body: JSON.stringify(signedCurrencyConversionRate),
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    }
  );
};

export const getTransactionHistories = async (cognitoId: string, accountType: string, accountId: string) => {
  return fetchJson<Array<TxHistory>>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/users/${encodeURIComponent(cognitoId)}/accounts/${encodeURIComponent(
      accountType
    )}/${accountId}/transactions`
  );
};

export const getTransactionHistoryDetail = (cognitoId: string, transactionType: DepositWithdraw, transactionId: string) => {
  return fetchJson<TxDetail<DepositWithdraw>>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/users/${encodeURIComponent(
      cognitoId
    )}/transactions/deposit-withdrawal/${transactionType}/${transactionId}`
  );
};

export const getSignedCurrencyConversionRate = (frCurrency: string, toCurrency: string) => {
  return fetchJson<SignedCurrencyConversionRate>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/signedCurrencyConversionRates/${frCurrency}/${toCurrency}`
  );
};

export const getWithdrawalDetails = async () => {
  const response = await fetch(`${API_CONFIG.PT_WEB_ENDPOINT}/labels/v4/withdrawal.json`);
  return response.json();
};

// The withdrawal charging service reads v3; v4 is used by the form for limits and currency choices.
export const getWithdrawalFeeDetails = async (): Promise<WithdrawalDetails> => {
  const response = await fetch(`${API_CONFIG.PT_WEB_ENDPOINT}/labels/v3/withdrawal.json`);
  if (!response.ok) throw new Error(`Withdrawal fee configuration unavailable: ${response.status}`);
  return response.json();
};

export const getWithdrawalInfo = () => {
  return fetchJson<WithdrawalInfo>(`${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/withdrawalRequests/withdrawalInfo`);
};

export const uploadDepositRequestImage = (depositRequest: DepositRequest, file: File) => {
  let formData = new FormData();
  formData.append('files', file);

  return fetchJson<DepositRequest>(`/aqt/cash/v1/depositRequests/${encodeURIComponent(depositRequest.id)}/images`, {
    body: formData,
    method: 'POST',
  });
};
